import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import { connect } from 'react-redux';
import { Table, Fade, Toolbar, Typography, AppBar, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibleIcon from '@mui/icons-material/Visibility';
import HiddenIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import Search from 'src/components/common/filters/Search';
import MassActionListTableBody from './MassActionListTableBody';

import { editMassAction, enableAction, checkEmptyFieldSelection } from 'src/reducers/massAction';
import utils from 'src/utils/utils';
import Headers from './Headers';

import filtrable from 'src/components/common/hocs/filtrable';
import { filterFn, quickFilterFn } from 'src/components/common/filters/campaignFiltersUtils';

import columnConfig from './utils';
import { REQUEST_CONFIG, REQUEST_RESULTS } from '../MassActionContent';
import { useLocation } from 'wouter';
import { useAlert } from 'src/hooks';

const useStyles = makeStyles()(theme => ({
  appBar: {
    position: 'relative',
  },
  tableWrapper: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    overflow: 'auto',
    flex: 1,
  },

  createBtn: {
    zIndex: 1,
    backgroundColor: theme.palette.primary.contrastText,
    color: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.contrastText,
      opacity: 0.9,
    },
  },
}));

const MassActionRequestList = ({
  goToStep,
  doCheckEmptyFieldSelection,
  doEditMassAction,
  clearEditedData,
  doEnableAction,
  filteredData,
  doFilter,
  addFilter,
  removeFilter,
  clearFilters,
  selectedFilters,
}) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({
    orderBy: 'creationDate',
    order: 'desc',
  });
  const [, navigate] = useLocation();
  const { classes } = useStyles();
  const [showMasked, setShowMasked] = useState(false);

  const [quickFilter, setQuickFilter] = useState('');

  const { showAlert } = useAlert();

  const { columnNames, columnsSorters } = columnConfig;

  const updateQuickFilter = useCallback(
    filterValue => {
      setQuickFilter(filterValue);
    },
    [setQuickFilter]
  );

  function editAction(massAction) {
    goToStep(REQUEST_CONFIG); // on s'assure que le step est sur la bonne page meme si on redirige car on rentre dans l'editeur qui reste géré par les step et on commence à ce step
    navigate(`/oneoff/${massAction.id}/edit`);
  }

  async function executeAction(massAction) {
    await doEditMassAction(massAction, showAlert);
    await doCheckEmptyFieldSelection(() => goToStep(REQUEST_RESULTS), true);
    navigate(`/oneoff/${massAction.id}/edit`);
  }

  function showHistory(massAction) {
    navigate(`/oneoff/${massAction.id}/history`);
  }

  const doSort = useCallback(
    column => {
      let order = 'asc';
      if (sortConfig.orderBy === column) {
        order = sortConfig.order === 'asc' ? 'desc' : 'asc';
      }
      setSortConfig({ order, orderBy: column });
    },
    [sortConfig]
  );

  function doFilterMasked() {
    if (showMasked) return filteredData;
    return filteredData.filter(ma => ma.enabled);
  }

  const getOptions = useCallback(
    ma => ['edit', 'execute', (ma.enabled && 'disable') || 'enable', 'showHistory'],
    []
  );

  function quickFiltered() {
    if (quickFilter) {
      const quickFilters = [{ key: 'name', value: quickFilter }];
      return doFilterMasked().filter(quickFilterFn(quickFilters));
    }
    return doFilterMasked();
  }

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: 'left',
        height: '100%',
      }}
    >
      <AppBar className={classes.appBar} color="primary">
        <Toolbar>
          <Typography variant="h5" color="inherit">
            {t(`smartmessaging.massAction.massActionList.title`)}
          </Typography>
          <div
            style={{
              flex: 1,
              marginLeft: '16px',
            }}
          >
            <Tooltip
              title={
                (!showMasked && t('smartmessaging.tooltip.showDisabledCampaigns')) ||
                t('smartmessaging.tooltip.hideDisabledCampaigns')
              }
            >
              <IconButton
                aria-label="clear"
                onClick={e => {
                  e.preventDefault();
                  setShowMasked(!showMasked);
                }}
                color="inherit"
                size="large"
              >
                {showMasked && <VisibleIcon />}
                {!showMasked && <HiddenIcon />}
              </IconButton>
            </Tooltip>
          </div>
          <Tooltip title={t('smartmessaging.tooltip.massAction.createRequest')}>
            <IconButton
              aria-label="clear"
              onClick={e => {
                e.preventDefault();
                clearEditedData();
                navigate('/oneoff/create');
              }}
              className={classes.createBtn}
              size="large"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Fade in timeout={500}>
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            overflow: 'hidden',
            textAlign: 'left',
          }}
        >
          <div style={{ margin: '8px' }}>
            <Search
              updateQuickFilter={updateQuickFilter}
              filterProps={{
                filteredData,
                doFilter,
                addFilter,
                removeFilter,
                clearFilters,
                selectedFilters,
              }}
            />
          </div>
          <div className={`${classes.tableWrapper}`}>
            <Table>
              <Headers
                columnNames={columnNames.filter(n =>
                  utils.isNodeMapped() ? true : ['networkNodeName'].indexOf(n) === -1
                )}
                sortConfig={sortConfig}
                doSort={doSort}
                addFilter={addFilter}
              />
              <MassActionListTableBody
                massActionList={[...quickFiltered()].sort(
                  columnsSorters[sortConfig.orderBy][sortConfig.order]
                )}
                getOptions={getOptions}
                actions={{
                  edit: editAction,
                  execute: executeAction,
                  enable: doEnableAction,
                  disable: doEnableAction,
                  showHistory,
                }}
              />
            </Table>
          </div>
        </div>
      </Fade>
    </div>
  );
};

MassActionRequestList.propTypes = {
  goToStep: PropTypes.func.isRequired,
  doEditMassAction: PropTypes.func.isRequired,
  clearEditedData: PropTypes.func.isRequired,
  doEnableAction: PropTypes.func.isRequired,
  filteredData: PropTypes.array.isRequired,
  doFilter: PropTypes.func.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  doCheckEmptyFieldSelection: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  massActions: state.massAction.massActions,
});

const actionCreators = {
  doEditMassAction: editMassAction,
  doCheckEmptyFieldSelection: checkEmptyFieldSelection,
  clearEditedData: () => ({
    type: 'CLEAR_EDITED_DATA',
  }),
  doEnableAction: enableAction,
};

const filterCfg = props => ({
  filterFn,
  filtrableData: props.massActions,
});

const FiltrableList = filtrable(filterCfg)(MassActionRequestList);

export default connect(mapStateToProps, actionCreators)(FiltrableList);
